import React, { useState } from 'react';
import PropTypes from 'prop-types';

import sendTestEmail from '../../../api/graphql/mutations/sendTestEmail';
import Notification, { useNotification } from '../../Notification';

// Test-send modal for the email template editor (Task 9, phase 3). Rendered
// inside the shared Modal.jsx by EmailTemplateEditor.jsx, which owns the
// open/closed boolean (Products' toggle pattern - see ProductsContainer.jsx).
//
// Sends the content currently in `draft` - the editor's in-memory state,
// INCLUDING any unsaved changes - never the last-saved template. That is the
// surprising part someone would otherwise assume away, so it is stated
// explicitly in the UI rather than left implicit. The archive BCC is
// suppressed server-side for test sends (sendTestEmail.js on the backend), so
// this never pollutes the production archive mailbox.
const EmailTestSendModal = ({ draft, templateId, onClose }) => {
  const [recipient, setRecipient] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [status, handleStatus] = useNotification();

  const handleSend = async () => {
    const trimmedRecipient = recipient.trim();
    if (!trimmedRecipient || isSending) {
      return;
    }

    setIsSending(true);
    const result = await sendTestEmail({
      recipient: trimmedRecipient,
      subject: draft.subject,
      htmlContent: draft.htmlContent,
      preheader: draft.preheader,
      textContent: draft.textContent,
      sampleDataJson: draft.sampleDataJson,
      templateId,
    });
    setIsSending(false);

    if (result && result.isAuth && result.success) {
      handleStatus('icon-checkbox', `Test email sent to ${trimmedRecipient}.`, 'green');
    } else if (result && result.isAuth) {
      handleStatus('icon-close-solid', result.error || 'Test send failed.', 'red');
    } else if (result) {
      handleStatus('icon-close-solid', 'You are not authorized to send test emails.', 'red');
    } else {
      handleStatus('icon-close-solid', 'Test send failed — check your connection and try again.', 'red');
    }
  };

  return (
    <div className="emails-app emails-modal-card">
      <div className="mstack">
        <div className="banner info">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#4c72ff" strokeWidth="1.7">
            <circle cx="8" cy="8" r="6.4" />
            <path d="M8 7.2v4M8 4.8h.01" strokeLinecap="round" />
          </svg>
          <span>
            This sends the content currently in the editor, including any unsaved changes —
            not the last version you saved.
          </span>
        </div>

        {status.text && <Notification {...status} />}

        <div className="field">
          <label htmlFor="email-test-send-recipient">
            Send to
            <input
              id="email-test-send-recipient"
              type="email"
              className="textinput"
              value={recipient}
              onChange={event => setRecipient(event.target.value)}
              placeholder="you@primaverasound.com"
              autoComplete="off"
            />
          </label>
          <p className="hint">
            The archive BCC is suppressed for test sends, so this never reaches the production
            archive mailbox.
          </p>
        </div>

        <div className="mactions">
          <button type="button" className="btn ghost" onClick={onClose}>Close</button>
          <button
            type="button"
            className="btn"
            onClick={handleSend}
            disabled={isSending || !recipient.trim()}
          >
            {isSending ? 'Sending…' : 'Send test'}
          </button>
        </div>
      </div>
    </div>
  );
};

EmailTestSendModal.propTypes = {
  draft: PropTypes.shape({
    subject: PropTypes.string,
    preheader: PropTypes.string,
    htmlContent: PropTypes.string,
    textContent: PropTypes.string,
    sampleDataJson: PropTypes.string,
  }).isRequired,
  templateId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EmailTestSendModal;
